"use client";

import { ctaClick } from "@/customAnalytics/trackingEvents";
import { LEAD_DROP_VARIANTS } from "@/constants/leadConstants";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import { cn } from "@/lib/utils";
import type { IProgramCardsCard, IProgramCardsCategory } from "@/types/page";
import { useRouter } from "next/navigation";
import Image from "next/image";
import { CSSProperties, FC, useMemo, useState } from "react";
import { useDispatch } from "react-redux";

const INITIAL_CARD_COUNT = 3;

/** Truthy check tolerant of 1 / "1" / true coming from the API. */
const isTruthy = (v: unknown): boolean => v === true || v === 1 || v === "1";

const isDefaultCategory = (
  cat: Pick<IProgramCardsCategory, "is_default"> | undefined
): boolean => isTruthy(cat?.is_default);

/** Synthetic "uncategorized" bucket the admin uses when categories are off. */
const isUncategorizedCategory = (
  cat: { id?: unknown; is_uncategorized_bucket?: boolean } | undefined
): boolean => Boolean(cat?.is_uncategorized_bucket) || Number(cat?.id) === -1;

/** Reference Arrow1 — a diagonal up-right arrow that rotates on hover. */
const Arrow: FC<{ className?: string; color?: string }> = ({
  className,
  color = "currentColor",
}) => (
  <svg
    className={className}
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M5.834 14.166 14.167 5.833m0 0H7.5m6.667 0v6.667"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

interface ProgramCards3Section extends Record<string, unknown> {
  id?: string | number;
  status?: boolean;
  desktop_background_color?: string;
  mobile_background_color?: string;
  pre_title?: string;
  pre_title_color?: string;
  title?: string;
  title_start_color?: string;
  use_categories?: boolean | number;
  card_background_color?: string;
  card_hover_background_color?: string;
  card_hover_text_color?: string;
  cta_text?: string;
  cta_text_color?: string;
  cta_background_start_color?: string;
  cta_icon?: string;
  cta_icon_alt?: string;
  categories?: IProgramCardsCategory[];
}

interface ProgramCards3Props {
  data?: { data?: ProgramCards3Section } | ProgramCards3Section;
}

/** Single program card — image with corner badge + overlapping body that flips colors on hover. */
const ProgramCard: FC<{
  card: IProgramCardsCard;
  normalBg: string;
  hoverBg: string;
  hoverText: string;
  onClick: (card: IProgramCardsCard) => void;
}> = ({ card, normalBg, hoverBg, hoverText, onClick }) => {
  const [hovered, setHovered] = useState(false);
  const badgeText = (card.badge_text || "").trim();

  const titleColor = hovered ? hoverText : card.title_color || "#171719";
  const subtitleColor = hovered ? hoverText : card.subtitle_color || "#6B7280";
  const linkColor = hovered ? hoverText : card.button_text_color || "#E1473F";

  const bodyStyle: CSSProperties = {
    backgroundColor: hovered ? hoverBg : normalBg,
    boxShadow: hovered
      ? "0 16px 40px rgba(225,71,63,0.28)"
      : "0 8px 24px rgba(0,0,0,0.08)",
  };

  return (
    <div
      className="relative h-[308px] w-[332px] shrink-0 cursor-pointer"
      onClick={() => onClick(card)}
    >
      {/* Image */}
      <div className="absolute top-0 z-0 h-[204px] w-full overflow-hidden rounded-lg bg-[#F4F4F4]">
        {card.image ? (
          <Image
            alt={card.image_alt || card.title || ""}
            width={332}
            height={204}
            src={card.image}
            className="h-full w-full object-cover"
          />
        ) : null}
      </div>

      {/* Corner badge */}
      {badgeText && (
        <div
          className="absolute right-0 top-0 z-20 rounded-bl-lg rounded-tr-lg px-9 py-1 text-[14px] font-medium"
          style={{
            color: card.badge_text_color || "#FFFFFF",
            backgroundColor: card.badge_background_color || "#FFA405",
          }}
        >
          {badgeText}
        </div>
      )}

      {/* Overlapping body */}
      <div
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        className="absolute left-1/2 top-[130px] z-10 w-[318px] -translate-x-1/2 rounded-lg px-4 pb-9 pt-5 transition-[background-color,box-shadow] duration-300 ease-in-out"
        style={bodyStyle}
      >
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <p
              className="font-poppins text-[22px] font-semibold leading-tight"
              style={{ color: titleColor }}
            >
              {card.title}
            </p>
            <p
              className="flex min-h-[48px] items-center font-poppins text-[15px] font-normal leading-snug"
              style={{ color: subtitleColor }}
            >
              {card.subtitle}
            </p>
          </div>
          <div
            className="flex items-center gap-1 font-poppins text-[16px] font-semibold"
            style={{ color: linkColor }}
          >
            {card.button_text || "Explore"}
            {card.button_icon ? (
              <Image
                src={card.button_icon}
                alt={card.button_icon_alt || ""}
                width={20}
                height={20}
                className={cn(
                  "size-5 object-contain transition-transform duration-700",
                  hovered ? "rotate-45" : "rotate-0"
                )}
              />
            ) : (
              <Arrow
                color={linkColor}
                className={cn(
                  "size-5 transition-transform duration-700",
                  hovered ? "rotate-45" : "rotate-0"
                )}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const ProgramCards3: FC<ProgramCards3Props> = ({ data }) => {
  const router = useRouter();
  const dispatch = useDispatch();

  const section = useMemo<ProgramCards3Section | undefined>(() => {
    if (!data) return undefined;
    const nested = (data as { data?: ProgramCards3Section }).data;
    return nested ?? (data as ProgramCards3Section);
  }, [data]);

  const categories = useMemo<IProgramCardsCategory[]>(
    () => (Array.isArray(section?.categories) ? section!.categories! : []),
    [section]
  );

  const visibleTabs = useMemo(
    () => categories.filter((c) => !isUncategorizedCategory(c)),
    [categories]
  );
  const showTabs = isTruthy(section?.use_categories) && visibleTabs.length > 0;

  const defaultIndex = Math.max(
    0,
    categories.findIndex((c) => isDefaultCategory(c))
  );
  const [activeIndex, setActiveIndex] = useState(defaultIndex);
  const [expanded, setExpanded] = useState(false);

  const activeCategory = categories[activeIndex] ?? categories[0];

  const displayedCards = useMemo<IProgramCardsCard[]>(() => {
    const source = isDefaultCategory(activeCategory)
      ? categories.flatMap((c) => c.cards || [])
      : activeCategory?.cards || [];
    return [...source].sort(
      (a, b) => (Number(a.priority) || 0) - (Number(b.priority) || 0)
    );
  }, [activeCategory, categories]);

  if (!section) return null;

  const navigate = (url?: string, label?: string) => {
    ctaClick({
      section: "explore_programs_section",
      component: "program_cards_3",
      label: label || "card",
      step: "",
      entity: "card_click",
      landing_url: url || "",
    });
    if (!url) {
      dispatch(handleOpenLeaddrop({ variant: LEAD_DROP_VARIANTS.REGISTER }));
      return;
    }
    if (url.startsWith("/")) router.push(url);
    else window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleCardClick = (card: IProgramCardsCard) =>
    navigate(card.course_url, card.title);

  const cardsToRender = expanded
    ? displayedCards
    : displayedCards.slice(0, INITIAL_CARD_COUNT);
  const canExpand = displayedCards.length > INITIAL_CARD_COUNT;

  const normalCardBg = section.card_background_color || "#FFFFFF";
  const hoverCardBg = section.card_hover_background_color || "#E1473F";
  const hoverCardText = section.card_hover_text_color || "#FFFFFF";

  return (
    <section
      className="flex w-full items-center justify-center px-4 min-[640px]:px-6"
      style={{ backgroundColor: section.desktop_background_color || "#FFFFFF" }}
    >
      <div className="flex w-full max-w-[1112px] flex-col items-center gap-[30px] py-8 min-[768px]:gap-[50px]">
        {/* Heading + tabs */}
        <div className="flex w-full flex-col items-center gap-[18px] px-1 min-[768px]:flex-row min-[768px]:justify-between">
          {(section.pre_title || section.title) && (
            <h2 className="text-center font-poppins text-[28px] font-semibold leading-tight min-[768px]:text-left min-[768px]:text-[32px]">
              {section.pre_title && (
                <span style={{ color: section.pre_title_color || "#171719" }}>
                  {section.pre_title}{" "}
                </span>
              )}
              {section.title && (
                <span style={{ color: section.title_start_color || "#E1473F" }}>
                  {section.title}
                </span>
              )}
            </h2>
          )}

          {showTabs && (
            <div className="flex w-fit items-center justify-center gap-3 rounded-[44px] bg-[#F4F4F4] p-1">
              {visibleTabs.map((cat, idx) => {
                const active = idx === activeIndex;
                return (
                  <div key={cat.id ?? `${cat.name}-${idx}`} className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setActiveIndex(idx);
                        setExpanded(false);
                      }}
                      className="cursor-pointer rounded-[36px] px-5 py-[10px] font-poppins text-[15px] font-medium transition-colors"
                      style={{
                        color: active
                          ? cat.active_text_color || "#FFFFFF"
                          : cat.text_color || "#171719",
                        backgroundColor: active
                          ? cat.active_background_color || "#E1473F"
                          : "transparent",
                      }}
                    >
                      {cat.name}
                    </button>
                    {idx < visibleTabs.length - 1 && (
                      <span className="h-[30px] w-px bg-[#D9D9D9]" />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Cards */}
        {cardsToRender.length > 0 && (
          <div
            className={cn(
              "flex w-full flex-wrap items-start gap-x-4 gap-y-11 min-[768px]:pb-2",
              showTabs ? "justify-center" : "justify-start"
            )}
          >
            {cardsToRender.map((card, idx) => (
              <ProgramCard
                key={card.id ?? idx}
                card={card}
                normalBg={normalCardBg}
                hoverBg={hoverCardBg}
                hoverText={hoverCardText}
                onClick={handleCardClick}
              />
            ))}
          </div>
        )}

        {/* View More / View Less */}
        {canExpand && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="flex items-center gap-[10px] rounded-[6px] px-7 py-3 font-poppins text-[15px] font-medium transition-opacity hover:opacity-90"
            style={{
              color: section.cta_text_color || "#FFFFFF",
              backgroundColor: section.cta_background_start_color || "#E1473F",
            }}
          >
            {expanded ? "View Less" : section.cta_text || "View More"}
            {section.cta_icon ? (
              <Image
                src={section.cta_icon}
                alt={section.cta_icon_alt || ""}
                width={20}
                height={20}
                className="size-5 object-contain"
              />
            ) : (
              <Arrow className="size-5" color={section.cta_text_color || "#FFFFFF"} />
            )}
          </button>
        )}
      </div>
    </section>
  );
};

export default ProgramCards3;
